import difference from 'lodash/difference';
import { useEffect, useState } from 'react';

// Types
import type { ColumnOrderState } from '@tanstack/react-table';

// Utils
import { getColumnOrderFromCookie, getVisibleColumns } from '@/utils/table/getColumnOrder';

type FormatVisibleColumnsProps<TColumn extends string> = Pick<
    UseTableColumnProps<TColumn>,
    'columnsVisibility' | 'nameCookieColumnVisibility'
>;

const formatVisibleColumn = <TColumn extends string>({
    columnsVisibility,
    nameCookieColumnVisibility
}: FormatVisibleColumnsProps<TColumn>) => {
    const data = localStorage.getItem(nameCookieColumnVisibility)
        ? (JSON.parse(localStorage.getItem(nameCookieColumnVisibility)) as typeof columnsVisibility)
        : columnsVisibility;

    const newColumnsAddedInConfigs = (
        difference(Object.keys(columnsVisibility), Object.keys(data)) as TColumn[]
    ).reduce(
        (current, column) => ({
            ...current,
            [column]: columnsVisibility[column]
        }),
        {} as FormatVisibleColumnsProps<TColumn>['columnsVisibility']
    );

    return { ...data, ...newColumnsAddedInConfigs };
};

type UseTableColumnProps<TColumn extends string> = {
    nameCookieColumnOrder: string;
    nameCookieColumnVisibility: string;
    columnsVisibility: Record<TColumn, boolean>;
};

const useTableColumnState = <TColumnVisibility extends string>({
    nameCookieColumnOrder,
    nameCookieColumnVisibility,
    columnsVisibility
}: UseTableColumnProps<TColumnVisibility>) => {
    // State for column visibility
    const [columnVisibility, setColumnVisibility] = useState<typeof columnsVisibility>(
        formatVisibleColumn({ columnsVisibility, nameCookieColumnVisibility })
    );

    // State for column order
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
        getColumnOrderFromCookie(nameCookieColumnOrder, columnVisibility)
    );

    useEffect(() => {
        const visibleColumns = formatVisibleColumn({
            columnsVisibility,
            nameCookieColumnVisibility
        });
        setColumnVisibility(visibleColumns);
        setColumnOrder(getColumnOrderFromCookie(nameCookieColumnOrder, visibleColumns));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(columnsVisibility), nameCookieColumnVisibility]);

    useEffect(() => {
        // Save column visibility in local storage
        localStorage.setItem(nameCookieColumnVisibility, JSON.stringify(columnVisibility));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(columnVisibility), nameCookieColumnVisibility]);

    useEffect(() => {
        localStorage.setItem(nameCookieColumnOrder, JSON.stringify(columnOrder));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(columnOrder), nameCookieColumnOrder]);

    useEffect(() => {
        const visibleColumns = getVisibleColumns(columnVisibility);

        // Add new column visible in the sorting state to be able to sort it
        if (visibleColumns.length > columnOrder.length) {
            const newColumns = difference(visibleColumns, columnOrder);
            return setColumnOrder((prevState) => [...prevState, ...newColumns]);
        }

        // Remove column from sorting state if it is not visible anymore
        const removedColumns = difference(columnOrder, visibleColumns);
        setColumnOrder((prevState) =>
            prevState.filter((column) => !removedColumns.includes(column))
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(columnVisibility)]);

    return { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder };
};

export default useTableColumnState;
